import { useWatch } from 'react-hook-form';
import { SchemaFormFields } from '../schemaForm';
import type { ZodAnyDiscriminatedUnion } from '../types';
import type { DiscriminatorValue, FieldsDefinition, RenderFieldsMap } from './types';

export interface DiscriminatedUnionFormDefinition<Schema extends ZodAnyDiscriminatedUnion> {
    fields: FieldsDefinition<Schema>;
    renderFields?: RenderFieldsMap<Schema>;
}

interface DiscriminatedUnionProps<Schema extends ZodAnyDiscriminatedUnion> {
    namePrefix?: string;
    schema: Schema;
    formDefinition:
        | DiscriminatedUnionFormDefinition<Schema>
        | ((discriminators: DiscriminatorValue<Schema>[]) => DiscriminatedUnionFormDefinition<Schema>);
}

export const DiscriminatedUnion = <Schema extends ZodAnyDiscriminatedUnion>({
    namePrefix,
    schema,
    formDefinition,
}: DiscriminatedUnionProps<Schema>) => {
    const { discriminator } = schema._def;
    const value = useWatch({ name: namePrefix ? `${namePrefix}.${discriminator}` : discriminator });
    const discriminators: DiscriminatorValue<Schema>[] = schema.options.map(
        (option) => option.shape[discriminator].value
    );
    const { fields, renderFields } =
        typeof formDefinition === 'function' ? formDefinition(discriminators) : formDefinition;

    return (
        <>
            {schema.options.map((option) => {
                const optionDiscriminator: DiscriminatorValue<Schema> = option.shape[discriminator].value;
                return value === optionDiscriminator ? (
                    <SchemaFormFields
                        key={optionDiscriminator}
                        namePrefix={namePrefix}
                        schema={option}
                        formDefinition={{
                            fields: fields as never,
                            renderFields:
                                renderFields?.[optionDiscriminator] ??
                                schema.optionsMap.get(optionDiscriminator)?.keyof().options,
                        }}
                    />
                ) : null;
            })}
        </>
    );
};
